/**
 * As contas de data da barra de relatórios — puras, fora do `.tsx`.
 *
 * A API resolve `de` e `ate` sem hora como meia-noite **do fuso do tenant**, e
 * `ate` como o começo do dia seguinte: o dia que a pessoa digitou entra
 * inteiro. A tela precisa concordar com isso em dois pontos, e os dois já
 * erraram antes:
 *
 *   • o campo "até" aponta para o dia desejado, e não para o seguinte — a
 *     gambiarra de somar um dia existia porque `ate` descartava o dia digitado;
 *   • o rótulo do recorte mostra o **último dia contado**, no fuso que a
 *     resposta declara. Formatar `recorte.ate` cru nomearia o dia seguinte, e
 *     formatá-lo no fuso do navegador nomearia o dia errado durante as horas
 *     em que os dois fusos discordam.
 */

const DIA = 86_400_000;

/**
 * `AAAA-MM-DD` do relógio de quem está olhando a tela — o formato do
 * `<input type="date">`.
 *
 * Componentes locais e não `toISOString()`: às 21h em Cuiabá o UTC já virou o
 * dia, e o atalho "últimos 30 dias" pediria um dia a mais na ponta.
 */
export function diaLocal(data: Date): string {
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  return `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;
}

/** O intervalo dos atalhos: os últimos N dias, com hoje incluído. */
export function ultimosDias(dias: number, agora = new Date()): { de: string; ate: string } {
  return { de: diaLocal(new Date(agora.getTime() - dias * DIA)), ate: diaLocal(agora) };
}

const formatadores = new Map<string, Intl.DateTimeFormat>();

function formatador(fuso: string): Intl.DateTimeFormat {
  const existente = formatadores.get(fuso);
  if (existente) return existente;
  const novo = new Intl.DateTimeFormat('pt-BR', {
    timeZone: fuso,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  formatadores.set(fuso, novo);
  return novo;
}

/** O primeiro dia contado, escrito no fuso do relatório. */
export function inicioDoRecorte(deIso: string, fuso: string): string {
  return formatador(fuso).format(new Date(deIso));
}

/**
 * O último dia contado, escrito no fuso do relatório.
 *
 * `ate` é exclusiva: um milissegundo antes dela é o último instante que a
 * consulta somou, e o dia desse instante é o que o leitor precisa ver.
 */
export function fimDoRecorte(ateIso: string, fuso: string): string {
  return formatador(fuso).format(new Date(new Date(ateIso).getTime() - 1));
}
